import React, { useCallback, useEffect } from 'react';
import { get } from 'lodash';
import { Translate } from 'core/translate';
import { translate } from '../../utils/translate/translations';
import BottomNavCGI from './BottomNavCGI';
import { ButtonText, Button } from 'core/controls/button/Button';
import { Link } from 'react-router-dom';
import { withNotifications } from '../../common/notifications/NotificationsContext';
import SvgIcon from '../../../core/icons/SvgIcon';
import { some, uniqBy, find } from 'lodash';
import { combineClassNames } from 'core/css';
import Currency from '../../common/intl/Currency';
import useResizeObserver from '../../hooks/useResizeObserver';

const Pricing = ({
  disclaimer,
  notifications,
  matrix,
  config,
  isSelectionRevealed,
  pricing,
  className,
}) => {
  const { show } = notifications;
  const { options, bodyTypes } = matrix;
  const { selections } = config;
  const mapped = options.map((option) => {
    const { cabSize, description } = find(bodyTypes, { id: option.bodyType });
    return {
      ...option,
      description,
      cabSize,
    };
  });
  const hasCabSizes = some(mapped, 'cabSize');
  const uniqueOptions = hasCabSizes
    ? uniqBy(mapped, 'cabSize')
    : uniqBy(mapped, 'description');

  const pricingLblKey = !isSelectionRevealed
    ? 'component.pricing.from.no.asterisk'
    : 'net.price';

  const calculatePrice = () => {
    if (!isSelectionRevealed) {
      if (hasCabSizes) {
        const priceItem =
          uniqueOptions.find(
            (uniqueOption) => uniqueOption.cabSize === selections.cabSize
          ) || {};
        return priceItem.message
          ? { message: priceItem.message }
          : { price: { text: <Currency value={priceItem.msrp} /> } };
      }
      const priceItem =
        uniqueOptions.find(
          (uniqueOption) => uniqueOption.bodyType === selections.bodyType
        ) || {};

      return priceItem.message
        ? { message: priceItem.message }
        : { price: { text: <Currency value={priceItem.msrp} /> } };
    }
    return { price: { text: pricing.normalizedNetPriceDisplay } };
  };
  const { price, message } = calculatePrice();

  if (pricing.accessoriesTotal) {
    const doubleDisclaimer = `
      <div><p><sup>†</sup> ${disclaimer}</p><br />
      <p><sup>‡</sup> ${translate('accessories.price.disclaimer')}<p></div>`;
    return (
      <a
        href="#"
        className={combineClassNames('bottom-nav__pricing', className)}
        onClick={(event) => {
          event.preventDefault();
          show(doubleDisclaimer, event, null, true);
        }}>
        <span
          className={'bottom-nav__total-price bottom-nav__pricing--top-gap'}>
          <label className="pointer bottom-nav__total-price-label">
            <Translate value={pricingLblKey} />{' '}
          </label>
          <output
            className="bottom-nav__total-price-currency"
            role="status"
            aria-live="polite">
            <span>{price.text}</span>
            <sup className="bottom-nav__total-dagger">†</sup>
          </output>
        </span>
        <div className="bottom-nav__accessory-price">
          <span className="bottom-nav__accessory-disclaimer">
            <span>
              +
              <span className="bottom-nav__dealer-price-currency">
                {pricing.accessoriesTotal.text}
                <sup className="bottom-nav__dealer-dagger">‡</sup>
              </span>{' '}
            </span>
          </span>
          <label className="pointer">
            <Translate value="bottom.nav.accessories.v3" />
          </label>
        </div>
      </a>
    );
  }

  const bottomNavDisclosure = get(
    config,
    'summary.credits.netPrice.disclaimers.summary',
    translate('byo.page.general.disclaimer.net.price.simple.v3')
  );

  return (
    <div className={combineClassNames('bottom-nav__pricing', className)}>
      <span className="bottom-nav__total-price">
        {!message && (
          <label className="bottom-nav__total-price-label">
            <Translate value={pricingLblKey} />{' '}
          </label>
        )}
        <a
          href="#"
          onClick={(event) => {
            event.preventDefault();
            show(bottomNavDisclosure, event, null, true);
          }}>
          <output
            className="bottom-nav__total-price-currency"
            role="status"
            aria-live="polite">
            {message ? <span>{message}</span> : <>{price.text}</>}
            <sup className="bottom-nav__total-dagger">†</sup>
          </output>
        </a>
      </span>
    </div>
  );
};

const BottomNavPricingSection = withNotifications(Pricing);

const BottomNavMoreViews = ({
  classIdentifier,
  location,
  displayJellyImg,
  displayMoreViewsLbl,
  moreViewsLbl,
  className,
  displayExterior = true,
  analyticsKey,
  angle,
}) => {
  const params = () => {
    let search = `?isExterior=${displayExterior}`;
    if (angle) {
      search = `${search}&angle=${angle}`;
    }
    return {
      pathname: `${location.pathname}/moreviews`,
      search,
    };
  };
  return (
    <div className={combineClassNames(['more-views-container', className])}>
      <Link className="more-views" to={params()}>
        {displayJellyImg && (
          <BottomNavCGI
            classIdentifier={classIdentifier}
            transparent
            imageSize="xSmall"
            analyticsKey={analyticsKey}
          />
        )}
        {displayMoreViewsLbl && (
          <div
            className={`stat-button-link more-views__control${
              displayJellyImg ? ' more-views__control--with-jelly' : ''
            }`}
            data-dtm={analyticsKey}>
            <SvgIcon
              className={`more-views__icon ${
                !displayJellyImg ? 'more-views__icon--no-jelly' : ''
              }`}
              name="more-views"
            />
            <span className="more-views__label">{moreViewsLbl}</span>
          </div>
        )}
      </Link>
    </div>
  );
};

const BottomNavPrimaryButtonLink = ({
  label,
  targetUrl,
  className,
  analyticsKey,
}) => (
  <Button
    as={Link}
    to={targetUrl}
    className={combineClassNames(
      'stat-button-link bottom-nav__button bottom-nav__button--next',
      className
    )}
    data-dtm={analyticsKey}>
    <ButtonText>{label}</ButtonText>
    <SvgIcon
      className="primary-inline-right__icon"
      name="primary-inline-right"
    />
  </Button>
);

const BottomNavPrimaryButton = ({
  onClick,
  label,
  className,
  analyticsKey,
  includeIcon = true,
}) => (
  <Button
    onClick={onClick}
    className={combineClassNames(
      'bottom-nav__button bottom-nav__button--next stat-button-link',
      className
    )}
    data-dtm={analyticsKey}>
    <ButtonText>{label}</ButtonText>
    {includeIcon && (
      <SvgIcon
        className="primary-inline-right__icon"
        name="primary-inline-right"
      />
    )}
  </Button>
);

const BottomNavSummaryButton = ({ className, analyticsKey }) => (
  <Button
    className={combineClassNames(
      'stat-button-link',
      'bottom-nav__button bottom-nav__button--summary',
      className
    )}
    as={Link}
    to={'summary'}
    data-dtm={analyticsKey}>
    <ButtonText className="bottom-nav__summary-label">
      <Translate value="summary" />
    </ButtonText>
  </Button>
);

const BottomNavButtonControls = ({ className, children }) => {
  return (
    <div
      className={combineClassNames('bottom-nav__button-container', className)}>
      {children}
    </div>
  );
};

const BottomNavContent = ({ children }) => (
  <div className="bottom-nav__row-1">{children}</div>
);

const BottomNav = ({
  containerClass,
  navClass,
  displayAccessories,
  children,
  setBottomOffset,
}) => {
  const [ref, { contentRect }] = useResizeObserver();
  const getContentRect = useCallback(
    (key) => contentRect && Math.round(contentRect[key]),
    [contentRect]
  );
  useEffect(() => {
    setBottomOffset(
      `${getContentRect('height') ? getContentRect('height') + 20 : 0}px`
    );
  }, [contentRect]);

  return (
    <>
      <div
        className={combineClassNames('bottom-nav-container', containerClass)}>
        <nav
          className={combineClassNames(
            'bottom-nav',
            containerClass,
            navClass,
            displayAccessories
          )}
          ref={ref}>
          {children}
        </nav>
      </div>
    </>
  );
};

export {
  BottomNavPrimaryButton,
  BottomNavPrimaryButtonLink,
  BottomNavSummaryButton,
  BottomNavButtonControls,
  BottomNavMoreViews,
  BottomNavPricingSection,
  BottomNavContent,
};

export default BottomNav;
